import { BCDataGrid } from '@bimser/components';
import { IDictionary, MLHelper } from '@bimser/core';
import IconAction from '@bimser/icons/16/action';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { IControlProperties } from '../../../../entities';
import {
	DataGridDisplayType,
	DataSourceType,
	GridLinesType,
	IDataSource,
	IOnLoadArgs,
	IPropertyField,
	SaveModeType
} from '../../../../entities/common';
import { calculateFormat } from '../../../../helper';
import CompositeDataBoundControl from '../../base/CompositeDataBoundControl';
import { IBatchOperation } from '../../entities/EventArgs';
import Styles from './assets/styles.scss';
import {
	IDataGridAlternateRowColorOptions,
	IDataGridColumn,
	IDataGridColumnOptions,
	IDataGridDetailRowOptions,
	IDataGridEditingOptions,
	IDataGridExecuteActionButton,
	IDataGridFilteringOptions,
	IDataGridGroupingOptions,
	IDataGridHiddenRowValueOptions,
	IDataGridPagingOptions,
	IDataGridProperties,
	IDataGridRowCollection,
	IDataGridRowDraggingOptions,
	IDataGridSearchingOptions,
	IDataGridSelectionOptions,
	IDataGridSortingOptions
} from './entities';
import {
	bindMethods,
	datePickerFormatter,
	dateTimePickerFormatter,
	generateButtonsColumn,
	generateColumns,
	generateToolbarButtonOptions,
	getPropertyFields,
	loadProperties,
	Methods,
	onBeforeToolbarItemClick,
	setDefaultProperties,
	timePickerFormatter
} from './helpers';

export default class DataGrid2 extends CompositeDataBoundControl {
	__bcDataGridRef = React.createRef<BCDataGrid.Entities.BCDataGridRef>();

	// Properties
	columns: IDataGridColumn<IControlProperties>[];
	rows: IDataGridRowCollection;
	dataSource: IDataSource;
	dataSourceType: DataSourceType;
	height: number;
	displayType: DataGridDisplayType;
	selectionForm: boolean;
	virtualScrolling: boolean;
	columnSettings: IDataGridColumnOptions;
	detailRow: IDataGridDetailRowOptions;
	editing: IDataGridEditingOptions;
	filtering: IDataGridFilteringOptions;
	grouping: IDataGridGroupingOptions;
	paging: IDataGridPagingOptions;
	searching: IDataGridSearchingOptions;
	selection: IDataGridSelectionOptions;
	sorting: IDataGridSortingOptions;
	alternateColor: IDataGridAlternateRowColorOptions;
	gridLines: GridLinesType;
	toolbarActionButtons: IDataGridExecuteActionButton<any>[];
	exportToExcel: boolean;
	showClearAllButton: boolean;
	gridBorder: boolean;
	hiddenRowColumnName: string;
	hiddenRowValueMatchers: IDataGridHiddenRowValueOptions;
	batchOperations: IBatchOperation[];
	hoverStateEnabled: boolean;
	selectedRowKeys: (number | string)[];
	selectedRowsData: IDictionary<any>[];
	columnAutoWidth: boolean;
	rowDragging: IDataGridRowDraggingOptions;
	wordWrapEnabled: boolean;
	columnHidingEnabled: boolean;
	columnFixingEnabled: boolean;
	saveMode: SaveModeType;
	cacheSettingsEnabled: boolean;

	// Public methods
	getUniqueDataField: typeof Methods.getUniqueDataField;
	newRow: typeof Methods.newRow;
	addColumns: typeof Methods.addColumns;
	getSelectedRows: typeof Methods.getSelectedRows;
	getCellValue: typeof Methods.getCellValue;
	getRowValues: typeof Methods.getRowValues;
	rowChangeAction: typeof Methods.rowChangeAction;
	deleteRows: typeof Methods.deleteRows;
	sortColumn: typeof Methods.sortColumn;
	reload: typeof Methods.reload;
	insertRow: typeof Methods.insertRow;
	insertRows: typeof Methods.insertRows;
	updateRow: typeof Methods.updateRow;
	updateRows: typeof Methods.updateRows;
	removeRow: typeof Methods.removeRow;
	removeRows: typeof Methods.removeRows;
	/**
	 * @deprecated
	 */
	updateColumnCells: typeof Methods.updateColumnCells;

	// Public properties
	pendingEdit: boolean = false;

	// Private methods
	__onChangesChange: typeof Methods.onChangesChange;
	__calculateHeight: typeof Methods.calculateHeight;
	__onInitNewRow: typeof Methods.onInitNewRow;
	__onContextMenuOpen: typeof Methods.onContextMenuOpen;
	__onExporting: typeof Methods.onExporting;
	__onEditorPreparing: typeof Methods.onEditorPreparing;
	__onRowDblClick: typeof Methods.onRowDblClick;
	__onRowRemoving: typeof Methods.onRowRemoving;
	__onRowRemoved: typeof Methods.onRowRemoved;
	__onRowInserting: typeof Methods.onRowInserting;
	__onRowInserted: typeof Methods.onRowInserted;
	__onRowUpdating: typeof Methods.onRowUpdating;
	__onRowUpdated: typeof Methods.onRowUpdated;
	__onSelectionChanged: typeof Methods.onSelectionChanged;
	__onSelectionChanging: typeof Methods.onSelectionChanging;
	__onEditingStart: typeof Methods.onEditingStart;
	__onCellPrepared: typeof Methods.onCellPrepared;
	__onEditCanceling: typeof Methods.onEditCanceling;
	__bindCustomStore: typeof Methods.bindCustomStore;
	__bindArrayStore: typeof Methods.bindArrayStore;
	__onSummaryChanged: typeof Methods.onSummaryChanged;
	__onContentReady: typeof Methods.onContentReady;
	__customSave: typeof Methods.customSave;
	__customLoad: typeof Methods.customLoad;
	__setSelectionResult: typeof Methods.setSelectionResult;
	__emitRelationEvents: typeof Methods.emitRelationEvents;
	__onReorder: typeof Methods.onReorder;

	// Private properties
	__store: BCDataGrid.Components.CustomStore | BCDataGrid.Components.DataSource;
	__fixCommitId: boolean = false;
	__comboBoxes: IDictionary<{
		store: BCDataGrid.Components.DataSource | BCDataGrid.Components.ArrayStore;
		comboBox?: BCDataGrid.Entities.SelectBoxRef;
		subscribed?: boolean;
		setLoading?: (loading: boolean) => void;
	}> = {};
	__toolbarButtonOptions = generateToolbarButtonOptions(this);
	__toolbarActionButtonOptions = [];
	__isNewRowEditing: boolean = false;
	__onLoadArgs: IOnLoadArgs = {};
	__formatterCache: Map<
		string,
		typeof dateTimePickerFormatter | typeof datePickerFormatter | typeof timePickerFormatter
	> = new Map();

	constructor() {
		super();

		bindMethods.call(this);
		setDefaultProperties.call(this);
	}

	__checkIncreaseCommitId(path: string[]): boolean {
		return this.__fixCommitId && path.includes('rows');
	}

	async __notifyDataGridRows(rows: IDataGridRowCollection) {
		// newRow metodunda dataGrid'e yeni bir satır eklenirken eğer
		// formula kullanıldı ise gridin tekrar render olması gerekiyor.
		if (this.columns.some(column => !!column.formula)) {
			return this.set('rows', rows);
		}

		this.__fixCommitId = true;
		await this.set('rows', rows);
		this.__fixCommitId = false;
	}

	__loadProperties(data: IDataGridProperties): void {
		super.__loadProperties(data);

		loadProperties.call(this, data);
	}

	__getPropertyFields(properties?: Map<string, any>): IPropertyField[] {
		const fields = super.__getPropertyFields();

		return getPropertyFields.call(this, fields, properties);
	}

	async __onLoad(args: IOnLoadArgs = {}) {
		await this.__bindCustomStore(args);
	}

	__getBCColumn<T extends IControlProperties>(name: string) {
		return this.columns.find(column => column.name === name) as IDataGridColumn<T>;
	}

	__retrieveDataGridInstance() {
		return this;
	}

	__onFormScrollStart() {
		super.__onFormScrollStart();

		this.__bcDataGridRef.current?.instance().hideColumnChooser();
	}

	__render(children?: React.ReactElement): React.ReactElement {
		if (children) {
			return children;
		}

		const isRemote = this.__store instanceof BCDataGrid.Components.CustomStore;

		const style: IDictionary<string> = {
			'--backgroundColor': this.style.backgroundColor
		};

		if (this.alternateColor.enabled) {
			style['--alternateColor'] = this.alternateColor.color;
		}

		const isReadOnly = this.__isReadOnly();
		const isDisabled = this.__isDisabled();

		const name: any = `Runtime_${this.__form.projectName}_${this.__form.name}_${this.name}`;

		return super.__render(
			<BCDataGrid.Components.DataGrid
				name={name}
				ref={this.__bcDataGridRef}
				className={Styles.dataGrid}
				columnAutoWidth={this.columnAutoWidth}
				disabled={isDisabled}
				showColumnLines={this.gridLines === 'vertical' || this.gridLines === 'both'}
				showRowLines={this.gridLines === 'horizontal' || this.gridLines === 'both'}
				remoteOperations={isRemote}
				dataSource={this.__store}
				hoverStateEnabled={this.hoverStateEnabled}
				selectedRowKeys={this.selectedRowKeys}
				onSelectionChanged={this.__onSelectionChanged}
				onContextMenuPreparing={this.__onContextMenuOpen}
				rowAlternationEnabled={this.alternateColor.enabled}
				allowColumnResizing={this.columnSettings.resizeable}
				columnResizingMode={this.columnSettings.resizingMode}
				onEditorPreparing={this.__onEditorPreparing}
				allowColumnReordering={this.columnSettings.orderable}
				columnHidingEnabled={this.columnHidingEnabled}
				wordWrapEnabled={this.wordWrapEnabled}
				onInitNewRow={this.__onInitNewRow}
				onExporting={this.__onExporting}
				showBorders={this.gridBorder}
				onRowDblClick={this.__onRowDblClick}
				onRowRemoving={this.__onRowRemoving}
				onRowRemoved={this.__onRowRemoved}
				onRowInserting={this.__onRowInserting}
				onRowInserted={this.__onRowInserted}
				onRowUpdating={this.__onRowUpdating}
				onRowUpdated={this.__onRowUpdated}
				onEditingStart={this.__onEditingStart}
				onCellPrepared={this.__onCellPrepared}
				onEditCanceling={this.__onEditCanceling}
				onContentReady={this.__onContentReady}
				height={this.__calculateHeight()}
				width="100%"
				style={style}
				cellHintEnabled
				repaintChangesOnly
			>
				<BCDataGrid.Components.StateStoring
					enabled={this.cacheSettingsEnabled}
					customSave={this.__customSave}
					customLoad={this.__customLoad}
					savingTimeout={500}
					storageKey={name}
					type="custom"
				/>
				{!this.virtualScrolling && (
					<BCDataGrid.Components.Paging
						enabled={this.paging.enabled ?? true}
						defaultPageIndex={this.paging.currentPage - 1}
						defaultPageSize={this.paging.pageSize}
					/>
				)}

				{!this.virtualScrolling && (
					<BCDataGrid.Components.Pager
						allowedPageSizes={this.paging.pagingSizes}
						showPageSizeSelector={this.paging.enabled}
						visible
						showInfo
						showNavigationButtons
					/>
				)}

				<BCDataGrid.Components.FilterRow
					visible={this.filtering.enabled}
					showOperationChooser={this.filtering.showOperationChooser}
				/>

				<BCDataGrid.Components.HeaderFilter visible={this.filtering.headerFilterEnabled} />

				<BCDataGrid.Components.SearchPanel visible={this.searching.enabled} />

				<BCDataGrid.Components.Export enabled={this.exportToExcel} />

				<BCDataGrid.Components.ColumnChooser
					enabled={this.columnSettings.showColumnChooser}
				/>

				<BCDataGrid.Components.ColumnFixing enabled={this.columnFixingEnabled} />

				<BCDataGrid.Components.Scrolling
					mode={this.virtualScrolling ? 'virtual' : 'standard'}
					showScrollbar="always"
				/>

				<BCDataGrid.Components.Sorting mode={this.sorting.mode} />

				{this.editing.mode && !isReadOnly && (
					<BCDataGrid.Components.Editing
						mode={this.editing.mode}
						allowAdding={this.editing.showAddCommand}
						allowUpdating={this.editing.showEditCommand}
						allowDeleting={this.editing.showDeleteCommand}
						onChangesChange={this.__onChangesChange}
					/>
				)}

				{this.selection.enabled && !isReadOnly && (
					<BCDataGrid.Components.Selection
						mode={this.selection.mode}
						selectAllMode={this.selection.selectAllMode}
						allowSelectAll={this.selection.showSelectAll}
						showCheckBoxesMode={this.selection.showCheckBoxesMode}
					/>
				)}

				{this.rowDragging.enabled && (
					<BCDataGrid.Components.RowDragging
						showDragIcons={this.rowDragging.showDragIcons}
						allowReordering={this.rowDragging.enabled}
						onReorder={this.__onReorder}
					/>
				)}

				<BCDataGrid.Components.Summary>
					{this.columns.flatMap(
						column =>
							column.summaryTypePrefixes?.map(summaryType => (
								<BCDataGrid.Components.TotalItem
									key={`${column.name}_${summaryType.type}`}
									column={column.name}
									summaryType={summaryType.type}
									valueFormat={calculateFormat(column)}
									displayFormat={
										summaryType.suffix
											? `${summaryType.suffix}: {0}`
											: undefined
									}
									customizeText={this.__onSummaryChanged.bind({
										self: this,
										column,
										summaryType
									})}
								/>
							))
					)}
				</BCDataGrid.Components.Summary>

				<BCDataGrid.Components.Toolbar>
					<BCDataGrid.Components.Item
						key="addRowButton"
						name="addRowButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.addRowButtonOptions}
					/>

					<BCDataGrid.Components.Item
						key="columnChooserButton"
						name="columnChooserButton"
						locateInMenu="auto"
					/>

					<BCDataGrid.Components.Item
						key="revertButton"
						name="revertButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.revertButtonOptions}
					/>

					<BCDataGrid.Components.Item
						key="saveButton"
						name="saveButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.saveButtonOptions}
						visible={false}
					/>

					<BCDataGrid.Components.Item
						key="exportButton"
						name="exportButton"
						locateInMenu="auto"
						options={this.__toolbarButtonOptions.exportButtonOptions}
					/>

					<BCDataGrid.Components.Item
						key="clearAll"
						widget="dxButton"
						location="after"
						options={this.__toolbarButtonOptions.clearAllOptions}
						locateInMenu="auto"
						visible={this.showClearAllButton}
						showText="inMenu"
					/>

					<BCDataGrid.Components.Item
						key="filterClear"
						widget="dxButton"
						location="after"
						options={this.__toolbarButtonOptions.filterClearOptions}
						locateInMenu="auto"
						visible={this.filtering.enabled}
						showText="inMenu"
					/>

					<BCDataGrid.Components.Item
						key="forceRefresh"
						widget="dxButton"
						location="after"
						options={this.__toolbarButtonOptions.forceRefreshOptions}
						locateInMenu="auto"
						visible={isRemote}
						showText="inMenu"
					/>

					<BCDataGrid.Components.Item
						key="searchPanel"
						name="searchPanel"
						locateInMenu="auto"
					/>

					{this.toolbarActionButtons.map((toolbarActionButton, index) => {
						const text = MLHelper.getMLText(toolbarActionButton.text);
						const hint = MLHelper.getMLText(toolbarActionButton.title);
						const icon = GetIcon16ByName(
							toolbarActionButton.icon,
							IconAction
						).info.getElement();

						if (!this.__toolbarActionButtonOptions.at(index)) {
							this.__toolbarActionButtonOptions.push({
								original: cloneDeep(toolbarActionButton),
								options: {
									hint,
									text,
									icon,
									onClick: () => {
										onBeforeToolbarItemClick(this, toolbarActionButton);
									}
								}
							});
						} else if (
							!isEqual(
								this.__toolbarActionButtonOptions.at(index).original,
								toolbarActionButton
							)
						) {
							this.__toolbarActionButtonOptions[index] = {
								original: cloneDeep(toolbarActionButton),
								options: {
									hint,
									text,
									icon,
									onClick: () => {
										onBeforeToolbarItemClick(this, toolbarActionButton);
									}
								}
							};
						}

						return (
							<BCDataGrid.Components.Item
								key={toolbarActionButton.name}
								visible={toolbarActionButton.visible}
								widget="dxButton"
								locateInMenu="auto"
								showText={'always'}
								location="before"
								disabled={
									toolbarActionButton.name === 'CREATERELATIONDOCUMENT' &&
									(isReadOnly || isDisabled)
								}
								options={this.__toolbarActionButtonOptions.at(index).options}
							/>
						);
					})}
				</BCDataGrid.Components.Toolbar>

				{generateButtonsColumn(this)}
				{generateColumns(this, this.columns)}
			</BCDataGrid.Components.DataGrid>
		);
	}
}
